import { Button } from "@/components/core/buttons/button";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { type CaseActivity, type CaseActivityDeleteSchema, activityDeleteSchema } from "@/features/case-evaluation/types/activities";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import Alert from "../core/alert";
import { Input } from "../core/forms/input";
import { Textarea } from "../core/forms/textarea";

export default function CaseActivityDeleteModal({
	data,
	handleSubmit,
}: {
	data?: CaseActivity;
	handleSubmit: values;
}) {
	const { closeModal } = useModalControl();
	const [error, setError] = useState(null);

	const form = useForm<CaseActivityDeleteSchema>({
		resolver: zodResolver(activityDeleteSchema),
		mode: "onChange",
		defaultValues: {
			id: data?.id,
			reason: "",
			password: "",
		},
	});

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full rounded-md border border-border/50">
						<div className="flex flex-col items-center justify-between w-full py-3 px-4 gap-3">
							<div className="grid grid-cols-12 w-full py-1 gap-x-4 gap-y-3">
								<div className="col-span-12 w-full">
									<Alert type="info" allowClose={false} title="Confirm Deletion">
										Are you sure you want to delete this activity '{data?.activity}'?
										{data.move_to_stage && (
											<span>Deleting this activity will revert the case stage to its previous stage and remove all subsequent activities.</span>
										)}
									</Alert>
								</div>
								<div className="col-span-12 w-full">
									<FormField
										control={form.control}
										name="reason"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Reason</FormLabel>
													<FormDescription />
												</div>
												<FormGroup>
													<Textarea {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-12 w-full">
									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Pin Code</FormLabel>
													<FormDescription>Enter your pin code to confirm and proceed with the submission.</FormDescription>
												</div>
												<FormGroup>
													<Input autoComplete="off" type="password" {...field} />
												</FormGroup>
												{error && <FormMessage>{error}</FormMessage>}
											</FormItem>
										)}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-row justify-between w-full pt-4">
					<Button
						className=""
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button className="" loading={form.formState.isSubmitting}>
						Confirm
					</Button>
				</div>
			</form>
		</Form>
	);
}
